import type { FunctionReturnType } from 'convex/server'

import { api } from '@/convex.generated/api'
import { convex } from '@/main'
import { router } from '@/app'

export interface AuthenticationContextType {
  user: FunctionReturnType<typeof api.auth.self> | null | undefined
  isAuthenticated: boolean
  isLoading: boolean
  signIn: (params: {
    email: string
    password: string
  }) => Promise<{ tokens: { token: string; refreshToken: string } | null }>
  signOut: () => Promise<void>
}

export async function loadAuthenticationContext(): Promise<AuthenticationContextType> {
  const user = await convex.query(api.auth.self, {})

  const namespace = convex.url
    .replaceAll('://', '')
    .replaceAll('-', '')
    .replaceAll('.', '')

  const JWT_STORAGE_KEY = `__convexAuthJWT_${namespace}`
  const REFRESH_TOKEN_STORAGE_KEY = `__convexAuthRefreshToken_${namespace}`

  const signIn: AuthenticationContextType['signIn'] = async (params: {
    email: string
    password: string
  }) => {
    const response = await (convex.action(api.auth.signIn, {
      provider: 'password',
      params: {
        email: params.email,
        password: params.password,
        flow: 'signUp',
      },
    }) as ReturnType<AuthenticationContextType['signIn']>)

    if (response.tokens) {
      localStorage.setItem(JWT_STORAGE_KEY, response.tokens.token)
      localStorage.setItem(
        REFRESH_TOKEN_STORAGE_KEY,
        response.tokens.refreshToken,
      )

      await router.invalidate()
    }

    return response
  }

  const signOut = async () => {
    try {
      await convex.action(api.auth.signOut, {})
    } catch (error) {
      // Ignore errors, usually means already signed out
    }

    localStorage.removeItem(JWT_STORAGE_KEY)
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)

    await router.invalidate()
  }

  return {
    user,
    isAuthenticated: Boolean(user),
    isLoading: false,
    signIn,
    signOut,
  }
}
